import { ModelFilterCategory } from '@store/zustand/model-filters';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

/**
 * Marks an element whose clicks must not dismiss the Filters popover: the
 * funnel trigger and the applied-filter chips open or re-target it themselves,
 * and a click-away in the same gesture would close it first and reopen it a
 * frame later.
 */
export const FILTERS_POPOVER_OWNER_ATTRIBUTE =
  'data-filters-popover-owner' as const;

export const filtersPopoverOwnerProps = {
  [FILTERS_POPOVER_OWNER_ATTRIBUTE]: '',
} as const;

type FiltersPopoverStoreType = {
  anchorEl: HTMLElement | null;
  fallbackAnchorEl: HTMLElement | null;
  category: ModelFilterCategory | null;
  criteriaId: string | undefined;
  openRoot: (anchorEl: HTMLElement) => void;
  openCategory: (category: ModelFilterCategory, anchorEl: HTMLElement) => void;
  showCategory: (category: ModelFilterCategory) => void;
  showRoot: () => void;
  setCriteriaId: (criteriaId: string | undefined) => void;
  setFallbackAnchor: (anchorEl: HTMLElement | null) => void;
  reanchorIfDetached: (anchorEl?: HTMLElement | null) => void;
  close: () => void;
};

/**
 * Where the Filters popover is open, and on which panel.
 *
 * Lives outside the popover component so a chip in the toolbar, or a row in
 * the +N popover, can open it directly on a category and anchored to
 * themselves. `anchorEl === null` means closed; `category === null` means the
 * root panel.
 *
 * The anchor can leave the document while the popover is open — a chip
 * cleared from its own panel, or a chip collapsing into +N when the row
 * narrows — and `Popper` would then draw the popover at the viewport origin.
 * `reanchorIfDetached` is the one rule for that: whoever notices the anchor is
 * gone passes the element the same category now lives in, and with none the
 * popover returns to `fallbackAnchorEl`, the funnel the popover registers
 * while mounted. The popover and the chip row both call it after their own
 * commits, because either can be the only component that re-rendered when
 * the anchor unmounted.
 */
const useFiltersPopoverStore = create<FiltersPopoverStoreType>((set, get) => ({
  anchorEl: null,
  fallbackAnchorEl: null,
  category: null,
  criteriaId: undefined,

  openRoot: (anchorEl) =>
    set({ anchorEl, category: null, criteriaId: undefined }),

  openCategory: (category, anchorEl) =>
    set({ anchorEl, category, criteriaId: undefined }),

  showCategory: (category) => set({ category, criteriaId: undefined }),

  showRoot: () => set({ category: null, criteriaId: undefined }),

  setCriteriaId: (criteriaId) => set({ criteriaId }),

  setFallbackAnchor: (fallbackAnchorEl) => {
    if (get().fallbackAnchorEl === fallbackAnchorEl) return;
    set({ fallbackAnchorEl });
  },

  reanchorIfDetached: (preferred) => {
    const { anchorEl, fallbackAnchorEl } = get();

    if (!anchorEl || anchorEl.isConnected) return;

    const next = preferred ?? fallbackAnchorEl;

    if (!next || next === anchorEl) return;

    set({ anchorEl: next });
  },

  close: () => {
    if (get().anchorEl === null) return;
    set({ anchorEl: null, category: null, criteriaId: undefined });
  },
}));

export const useFiltersPopover = () =>
  useFiltersPopoverStore(
    useShallow(({ anchorEl, category, criteriaId }) => ({
      anchorEl,
      category,
      criteriaId,
    })),
  );

export const useFiltersPopoverActions = () =>
  useFiltersPopoverStore(
    useShallow(
      ({
        openRoot,
        openCategory,
        showCategory,
        showRoot,
        setCriteriaId,
        setFallbackAnchor,
        reanchorIfDetached,
        close,
      }) => ({
        openRoot,
        openCategory,
        showCategory,
        showRoot,
        setCriteriaId,
        setFallbackAnchor,
        reanchorIfDetached,
        close,
      }),
    ),
  );

export const {
  getState: getFiltersPopoverState,
  setState: setFiltersPopoverState,
} = useFiltersPopoverStore;

export default useFiltersPopoverStore;
